import IdentityHeader from "@/components/common/IdentityHeader";
import { NavigationTabs } from "@/components/common/NavigationTabs";
import DataTableActionMenu from "@/components/datatable/components/TableActionMenu";
import ProfileSkeleton from "@/components/preview/ProfileSkeleton";
import StatStrip from "@/components/common/StatStrip";
import useConfirm from "@/hooks/useConfirm";
import useFormatter from "@/hooks/useFormatter";
import { getEmployeeAvatarColor, getInitials } from "@/lib/utils";
import { ateliersKeys, kpisKeys } from "@/query-keys";
import { services } from "@/services";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { BanknoteArrowUp, Calendar, Clock, Hammer, MapPin, User } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useAtelierQuery from "../_hooks/useAtelierQuery";
import useAtelierMutation from "../_hooks/useAtelierMutation";
import useAtelierKpis from "../_hooks/useAtelierKpis";

export const Route = createFileRoute("/_private/ateliers/$atelierId")({
  staticData: {
    breadcrumb: (matched) => ({ label: matched.loaderData?.name, translate: false }),
  },
  loader: async ({ params, context: { queryClient } }) => {
    const atelier = await queryClient.ensureQueryData({
      queryKey: ateliersKeys.detail(params.atelierId),
      queryFn: () => services.getAtelier(params.atelierId),
    });
    queryClient.ensureQueryData({
      queryKey: kpisKeys.ateliers(params.atelierId),
      queryFn: () => services.getAtelierKpis(params.atelierId),
    });
    return atelier;
  },
  component: RouteComponent,
  pendingComponent: ProfileSkeleton,
});

const TABS = [
  { value: "igold-transactions", icon: Hammer, labelKey: "kpis.igoldTransactions" },
  { value: "icash-transactions", icon: BanknoteArrowUp, labelKey: "kpis.icashTransactions" },
] as const;

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useParams();
  const loaderData = Route.useLoaderData();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { t } = useTranslation();
  const { weight, currency, date } = useFormatter();
  const { data } = useAtelierQuery(params.atelierId, loaderData);
  const { deleteAtelier, isDeleting } = useAtelierMutation();
  const { data: kpis } = useAtelierKpis(params.atelierId);

  const ateliersTabs = useMemo(() => TABS.map((tab) => ({ ...tab, label: t(tab.labelKey) })), [t]);

  const getCurrentTab = () => {
    if (pathname.includes("/icash-transactions")) return "icash-transactions";
    return "igold-transactions";
  };

  const handleTabChange = (value: string) => {
    switch (value) {
      case "icash-transactions":
        navigate({ to: "/ateliers/$atelierId/icash-transactions", params });
        break;
      default:
        navigate({ to: "/ateliers/$atelierId/igold-transactions", params });
        break;
    }
  };

  if (!data) return null;

  const atelier = data;
  const isDeleted = !!atelier.deletedAt;

  const deleteConfirm = useConfirm({
    title: t("ateliers.confirm.delete.title"),
    description: t("ateliers.confirm.delete.description"),
    isPending: isDeleting,
  });

  const colorClass = getEmployeeAvatarColor(atelier.id || atelier.name || "");

  return (
    <>
      <IdentityHeader
        initials={getInitials(atelier.name)}
        avatarColorClass={!isDeleted ? colorClass : undefined}
        name={atelier.name}
        subLabel={
          atelier.location ? (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3 shrink-0" />
              {atelier.location}
            </span>
          ) : null
        }
        metaLabel={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {t("ateliers.fields.createdAt")} {date(atelier.createdAt)}
            </span>
            {atelier.updatedAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t("ateliers.fields.updatedAt")} {date(atelier.updatedAt)}
              </span>
            )}
            {atelier.createdByName && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3 w-3" />
                {t("ateliers.fields.createdBy")} {atelier.createdByName}
              </span>
            )}
          </span>
        }
        isDeleted={isDeleted}
      >
        <DataTableActionMenu
          onDelete={() => deleteConfirm.confirm(() => void deleteAtelier(atelier.id))}
          row={atelier}
        />
      </IdentityHeader>

      {kpis && (
        <StatStrip
          columns={[
            {
              dotClass: "bg-muted-foreground",
              label: t("kpis.atelierIcashTotalExpenses"),
              value: currency(kpis.icashTotalExpenses),
              subValue: (
                <span>
                  {t("kpis.atelierIcashTotalWithdrawn")}: {currency(kpis.icashTotalWithdrawn)}
                  {" · "}
                  {t("kpis.atelierIcashTotalLosses")}: {currency(kpis.icashTotalLosses)}
                </span>
              ),
            },
            {
              dotClass: "bg-primary",
              label: t("kpis.atelierActiveGoldOutstanding"),
              value: weight(kpis.activeGoldOutstanding),
              subValue: (
                <span>
                  {t("kpis.atelierIgoldUserTotalLost")}: {weight(kpis.igoldUserTotalLost)}
                  {" · "}
                  {t("kpis.atelierIgoldEmployeeTotalLost")}: {weight(kpis.igoldEmployeeTotalLost)}
                </span>
              ),
            },
          ]}
        />
      )}

      <NavigationTabs activeTab={getCurrentTab()} onTabChange={handleTabChange} tabs={ateliersTabs}>
        <Outlet />
      </NavigationTabs>

      {deleteConfirm.confirmDialog}
    </>
  );
}
